import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { useAuth } from '@shared/state/hooks/useAuth';
import { Gender } from '@shared/types';
import { Card, Descriptions, Input, Modal, Select, Tag, message } from 'antd';

interface InfoForm {
  name: string;
  phoneNumber: string;
  gender: string;
  birth: string;
}

const emptyInfoForm: InfoForm = {
  name: '',
  phoneNumber: '',
  gender: '',
  birth: '',
};

//* 전화번호 하이픈 포맷 (숫자만 추출 후 010-1234-5678 형태)
const formatPhoneNumber = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length < 4) return digits;
  if (digits.length < 8) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
};

const genderLabel = (gender: string) => {
  if (gender === Gender.Male) return '남';
  if (gender === Gender.Femail) return '여';
  return '-';
};

interface Props {
  //* 상단 타이틀바가 없는 화면(gift-seller 등)에서 페이지 제목을 직접 표시할 때 사용
  heading?: string;
}

export const AccountSettings: FC<Props> = function AccountSettings({ heading }: Props) {
  const {
    userProfile,
    patchMe,
    patchMePassword,
    verifyCurrentPassword,
    refreshUserProfile,
  } = useAuth();

  // ─── 기본정보 (보기 → 수정 토글) ───
  const [editing, setEditing] = useState(false);
  const [infoForm, setInfoForm] = useState<InfoForm>(emptyInfoForm);
  const [infoSaving, setInfoSaving] = useState(false);

  const profileInfo = useMemo<InfoForm>(
    () => ({
      name: userProfile?.info?.name ?? '',
      phoneNumber: userProfile?.info?.phoneNumber ?? '',
      gender: userProfile?.info?.gender ?? '',
      birth: userProfile?.info?.birth ?? '',
    }),
    [userProfile],
  );

  // 편집모드 진입 시 현재 값으로 폼 초기화
  useEffect(() => {
    if (!editing) setInfoForm(profileInfo);
  }, [editing, profileInfo]);

  const infoDirty = useMemo(
    () =>
      infoForm.name !== profileInfo.name ||
      infoForm.phoneNumber !== profileInfo.phoneNumber ||
      infoForm.gender !== profileInfo.gender ||
      infoForm.birth !== profileInfo.birth,
    [infoForm, profileInfo],
  );

  const handleStartEdit = useCallback(() => {
    setInfoForm(profileInfo);
    setEditing(true);
  }, [profileInfo]);

  const handleCancelEdit = useCallback(() => {
    setInfoForm(profileInfo);
    setEditing(false);
  }, [profileInfo]);

  const handleSaveInfo = useCallback(async () => {
    if (!infoForm.name.trim()) {
      message.warning('이름을 입력해주세요.');
      return;
    }
    if (!userProfile?.info) return;
    if (!infoDirty) {
      setEditing(false);
      return;
    }
    setInfoSaving(true);
    try {
      const updated = await patchMe({
        info: {
          ...userProfile.info,
          name: infoForm.name.trim(),
          phoneNumber: infoForm.phoneNumber.trim(),
          gender: infoForm.gender,
          birth: infoForm.birth.trim(),
        },
      });
      if (updated) {
        await refreshUserProfile();
        message.success('기본정보가 저장되었습니다.');
        setEditing(false);
      } else {
        message.error('기본정보 저장에 실패했습니다.');
      }
    } finally {
      setInfoSaving(false);
    }
  }, [infoForm, infoDirty, patchMe, refreshUserProfile, userProfile]);

  // ─── 비밀번호 변경 (현재 비밀번호 확인 모달 → 새 비밀번호 입력) ───
  const [pwModalOpen, setPwModalOpen] = useState(false);
  const [pwVerifying, setPwVerifying] = useState(false);
  const [pwUnlocked, setPwUnlocked] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [pwSaving, setPwSaving] = useState(false);

  const openPwModal = useCallback(() => {
    setCurrentPassword('');
    setPwModalOpen(true);
  }, []);

  // 현재 비밀번호 검증 → 성공해야 새 비밀번호 입력 활성화
  const handleVerifyCurrent = useCallback(async () => {
    if (!currentPassword) {
      message.warning('현재 비밀번호를 입력해주세요.');
      return;
    }
    setPwVerifying(true);
    try {
      const verified = await verifyCurrentPassword(currentPassword);
      if (!verified) {
        message.error('현재 비밀번호가 일치하지 않습니다.');
        return;
      }
      setPwUnlocked(true);
      setPwModalOpen(false);
    } finally {
      setPwVerifying(false);
    }
  }, [currentPassword, verifyCurrentPassword]);

  const handleCancelPwChange = useCallback(() => {
    setPwUnlocked(false);
    setCurrentPassword('');
    setPassword('');
    setPasswordConfirm('');
  }, []);

  const handleChangePassword = useCallback(async () => {
    if (password.length < 4 || password.length > 16) {
      message.warning('새 비밀번호는 4~16자리로 입력해주세요.');
      return;
    }
    if (password !== passwordConfirm) {
      message.warning('새 비밀번호와 비밀번호 확인이 일치하지 않습니다.');
      return;
    }
    if (password === currentPassword) {
      message.warning('현재 비밀번호와 다른 비밀번호를 입력해주세요.');
      return;
    }
    setPwSaving(true);
    try {
      const ok = await patchMePassword(password);
      if (ok) handleCancelPwChange();
    } finally {
      setPwSaving(false);
    }
  }, [password, passwordConfirm, currentPassword, patchMePassword, handleCancelPwChange]);

  return (
    <div className="mx-auto max-w-4xl px-4 pb-8 sm:px-6 lg:px-8">
      <div className="space-y-6 py-6">
        {heading && (
          <h1 className="text-xl font-bold text-gray-800">{heading}</h1>
        )}

        {/* ─── 계정 정보 (읽기 전용) ─── */}
        <Card title="계정 정보">
          <Descriptions column={{ xs: 1, sm: 2 }} bordered size="small">
            <Descriptions.Item label="아이디">
              {userProfile?.username ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label="가입일">
              {userProfile?.createdAt
                ? new Date(userProfile.createdAt).toLocaleDateString('ko-KR')
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="권한" span={2}>
              <div className="flex flex-wrap gap-1">
                {(userProfile?.roles ?? []).map((role) => (
                  <Tag key={role}>{role}</Tag>
                ))}
              </div>
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {/* ─── 기본정보 (보기 → 수정 토글) ─── */}
        <Card
          title="기본정보"
          extra={
            editing ? (
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={infoSaving}
                  className="rounded border border-gray-300 bg-white px-3 py-1 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                  onClick={handleCancelEdit}
                >
                  취소
                </button>
                <button
                  type="button"
                  disabled={infoSaving || !infoDirty}
                  className="rounded bg-blue-600 px-3 py-1 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                  onClick={handleSaveInfo}
                >
                  저장
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="rounded border border-blue-300 bg-white px-3 py-1 text-sm text-blue-600 hover:bg-blue-50"
                onClick={handleStartEdit}
              >
                수정
              </button>
            )
          }
        >
          {editing ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium">이름</label>
                <Input
                  placeholder="이름"
                  value={infoForm.name}
                  onChange={(e) =>
                    setInfoForm((prev) => ({ ...prev, name: e.target.value }))
                  }
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">전화번호</label>
                <Input
                  placeholder="010-1234-5678"
                  value={infoForm.phoneNumber}
                  onChange={(e) =>
                    setInfoForm((prev) => ({
                      ...prev,
                      phoneNumber: formatPhoneNumber(e.target.value),
                    }))
                  }
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">성별</label>
                <Select
                  className="w-full"
                  placeholder="성별 선택"
                  allowClear
                  value={infoForm.gender || undefined}
                  onChange={(val) =>
                    setInfoForm((prev) => ({ ...prev, gender: val ?? '' }))
                  }
                  options={[
                    { value: Gender.Male, label: '남' },
                    { value: Gender.Femail, label: '여' },
                  ]}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">생년월일</label>
                <input
                  type="date"
                  className="ant-input w-full rounded border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none"
                  value={infoForm.birth}
                  onChange={(e) =>
                    setInfoForm((prev) => ({ ...prev, birth: e.target.value }))
                  }
                />
              </div>
            </div>
          ) : (
            <Descriptions column={{ xs: 1, sm: 2 }} bordered size="small">
              <Descriptions.Item label="이름">
                {profileInfo.name || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="전화번호">
                {profileInfo.phoneNumber || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="성별">
                {genderLabel(profileInfo.gender)}
              </Descriptions.Item>
              <Descriptions.Item label="생년월일">
                {profileInfo.birth || '-'}
              </Descriptions.Item>
            </Descriptions>
          )}
        </Card>

        {/* ─── 비밀번호 변경 ─── */}
        <Card
          title="비밀번호 변경"
          extra={
            pwUnlocked ? (
              <button
                type="button"
                disabled={pwSaving}
                className="rounded border border-gray-300 bg-white px-3 py-1 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                onClick={handleCancelPwChange}
              >
                취소
              </button>
            ) : (
              <button
                type="button"
                className="rounded border border-blue-300 bg-white px-3 py-1 text-sm text-blue-600 hover:bg-blue-50"
                onClick={openPwModal}
              >
                비밀번호 변경
              </button>
            )
          }
        >
          {pwUnlocked ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium">새 비밀번호</label>
                  <Input.Password
                    placeholder="4~16자리"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium">
                    새 비밀번호 확인
                  </label>
                  <Input.Password
                    placeholder="새 비밀번호 확인"
                    autoComplete="new-password"
                    value={passwordConfirm}
                    onChange={(e) => setPasswordConfirm(e.target.value)}
                    onPressEnter={handleChangePassword}
                  />
                </div>
              </div>
              <div className="flex justify-end pt-2">
                <button
                  type="button"
                  disabled={pwSaving || !password || !passwordConfirm}
                  className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                  onClick={handleChangePassword}
                >
                  변경 완료
                </button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500">
              비밀번호를 변경하려면 우측 상단 ‘비밀번호 변경’을 눌러 현재 비밀번호를
              확인하세요.
            </p>
          )}
        </Card>
      </div>

      {/* ─── 현재 비밀번호 확인 모달 ─── */}
      <Modal
        title="현재 비밀번호 확인"
        open={pwModalOpen}
        onOk={handleVerifyCurrent}
        onCancel={() => setPwModalOpen(false)}
        confirmLoading={pwVerifying}
        okText="확인"
        cancelText="취소"
        okButtonProps={{
          style: { backgroundColor: '#2563eb', borderColor: '#2563eb', color: '#fff' },
        }}
        destroyOnClose
      >
        <div className="space-y-2 py-2">
          <label className="block text-sm font-medium">현재 비밀번호</label>
          <Input.Password
            placeholder="현재 비밀번호를 입력하세요"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            onPressEnter={handleVerifyCurrent}
          />
          <p className="text-xs text-gray-400">
            현재 비밀번호가 일치해야 새 비밀번호를 입력할 수 있습니다.
          </p>
        </div>
      </Modal>
    </div>
  );
};
